import { studentDay } from '../utils/studentDates';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Ensure Axios points to the API server (adjust if you use a dev proxy)
if (!axios.defaults.baseURL) {
  axios.defaults.baseURL = 'http://localhost:5050';
}

// --- Helpers ---
const fmtDate = value => {
  const day = studentDay(value);
  return day ? new Intl.DateTimeFormat('en-IN', {day:'2-digit',month:'short',year:'numeric',timeZone:'UTC'}).format(new Date(`${day}T00:00:00Z`)) : '—';
};
const toISODateOnly = studentDay;
const toYMDInTZ = studentDay;

// Match your existing definition of “new admission”: a payment that has an admissionFee line
const isAdmissionPayment = (p) => (p?.feeLines || []).some(l => l?.type === 'admissionFee');

// Missing historical business dates remain unknown; save timestamps are not substitutes.
const getAdmissionDate = (p) => p?.date || null;

// Joining date should come from the Student record
const getJoiningDate = (student) => student?.joinDate || null;

const getStudentName = (student) => student?.name || '—';
const getStudentId = (student) => student?.studentId || '—';
const getWhatsapp = (student) => student?.whatsapp || student?.phone || '—';
const getCourseLabel = (student) => student?.course?.name || '_';
const getTeacherLabel = (student) => student?.course?.teacher || student?.teacher || '-';

const getModeLabel = (p) => p?.paymentMode || p?.mode || '-';

const getCollectedAmount = (p) => {
  const n = Number(p?.totalAmount ?? p?.amount ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const getPaymentTypeLabel = (p) => {
  // Keep it explicit for this report
  return isAdmissionPayment(p) ? 'Admission Fee' : '—';
};

export default function NewAdmission() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Date-range filters
  const [admissionFrom, setAdmissionFrom] = useState('');
  const [admissionTo, setAdmissionTo] = useState('');
  const [joinFrom, setJoinFrom] = useState('');
  const [joinTo, setJoinTo] = useState('');

  // Optional quick search
  const [q, setQ] = useState('');

  // Fetch data using the SAME approach as PaymentList (no special backend route)
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [payRes, stuRes] = await Promise.all([
          axios.get('/api/payments'),
          axios.get('/api/students'),
        ]);

        if (cancelled) return;

        const allPayments = Array.isArray(payRes.data)
          ? payRes.data
          : (payRes.data?.payments || payRes.data?.data || []);

        const allStudents = Array.isArray(stuRes.data) ? stuRes.data : [];

        setStudents(allStudents);

        // Only admission payments are relevant for this report
        const admissionPayments = allPayments.filter(isAdmissionPayment);
        setRows(admissionPayments);
      } catch (e) {
        console.error('NewAdmission load failed:', e?.response?.data || e.message);
        setError('Could not load new admission report data. Please check backend /api/payments and /api/students.');
        setRows([]);
        setStudents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, []);

  const studentMap = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(String(s._id), s);
    return map;
  }, [students]);

  const filteredRows = useMemo(() => {
    const query = q.trim().toLowerCase();

    return rows
      .filter((r) => {
        const sid = typeof r.studentId === 'object' ? r.studentId?._id : r.studentId;
        const student = (typeof r.studentId === 'object' && r.studentId?.name) ? r.studentId : studentMap.get(String(sid));

        const aDate = getAdmissionDate(r);
        const jDate = getJoiningDate(student);

        const aDay = studentDay(aDate), jDay = studentDay(jDate);
        const admissionOk = (!admissionFrom && !admissionTo) || Boolean(aDay && (!admissionFrom || aDay >= admissionFrom) && (!admissionTo || aDay <= admissionTo));
        const joinOk = (!joinFrom && !joinTo) || Boolean(jDay && (!joinFrom || jDay >= joinFrom) && (!joinTo || jDay <= joinTo));

        if (!admissionOk || !joinOk) return false;

        if (!query) return true;
        const hay = [
          getStudentName(student),
          getStudentId(student),
          getWhatsapp(student),
          getCourseLabel(student),
          getTeacherLabel(student),
          getModeLabel(r),
          getPaymentTypeLabel(r),
          toYMDInTZ(getAdmissionDate(r)),
        ]
          .join(' | ')
          .toLowerCase();
        return hay.includes(query);
      })
      .sort((a, b) => {
        // Sort by Admission Date desc
        const ta = new Date(getAdmissionDate(a) || 0).getTime();
        const tb = new Date(getAdmissionDate(b) || 0).getTime();
        return tb - ta;
      });
  }, [rows, admissionFrom, admissionTo, joinFrom, joinTo, q, studentMap]);

  const totalCollected = useMemo(() => {
    return filteredRows.reduce((sum, r) => sum + getCollectedAmount(r), 0);
  }, [filteredRows]);

  const handleReset = () => {
    setAdmissionFrom('');
    setAdmissionTo('');
    setJoinFrom('');
    setJoinTo('');
    setQ('');
  };

  const csvEscape = (val) => {
    const s = String(val ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleDownloadCSV = () => {
    const headers = [
      'Student Name',
      'Student ID',
      'Admission Date',
      'Joining Date',
      'Payment Type',
      'Course',
      'Teacher',
      'WhatsApp',
      'Mode',
      'Collected (INR)'
    ];

    const body = filteredRows.map((p) => {
      const sid = typeof p.studentId === 'object' ? p.studentId?._id : p.studentId;
      const student = (typeof p.studentId === 'object' && p.studentId?.name)
        ? p.studentId
        : studentMap.get(String(sid));

      return [
        getStudentName(student),
        getStudentId(student),
        toISODateOnly(getAdmissionDate(p)),
        toISODateOnly(getJoiningDate(student)),
        getPaymentTypeLabel(p),
        getCourseLabel(student),
        getTeacherLabel(student),
        getWhatsapp(student),
        getModeLabel(p),
        getCollectedAmount(p)
      ];
    });

    const csv = [headers, ...body]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `new_admission_report_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold">New Admission Report</h2>
          <p className="text-sm text-gray-600">
            Filter by <strong>Admission Date</strong> and <strong>Joining Date</strong>, then export.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleDownloadCSV}
            className="px-3 py-2 rounded bg-black text-white text-sm"
            disabled={loading || filteredRows.length === 0}
          >
            Download CSV
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-2 rounded border text-sm"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Admission From</label>
            <input
              type="date"
              aria-label="Admission From"
              value={admissionFrom}
              onChange={(e) => setAdmissionFrom(e.target.value)}
              className="w-full border rounded px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Admission To</label>
            <input
              type="date"
              aria-label="Admission To"
              value={admissionTo}
              onChange={(e) => setAdmissionTo(e.target.value)}
              className="w-full border rounded px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Joining From</label>
            <input
              type="date"
              aria-label="Joining From"
              value={joinFrom}
              onChange={(e) => setJoinFrom(e.target.value)}
              className="w-full border rounded px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Joining To</label>
            <input
              type="date"
              aria-label="Joining To"
              value={joinTo}
              onChange={(e) => setJoinTo(e.target.value)}
              className="w-full border rounded px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Search</label>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="name / id / phone / course"
              className="w-full border rounded px-2 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
          <div className="text-gray-700">
            Showing <strong>{filteredRows.length}</strong> records
          </div>
          <div className="text-gray-700">
            Total Collected: <strong>INR {Number(totalCollected).toLocaleString()}</strong>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 border border-red-300 bg-red-50 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-[1100px] w-full table-auto border-collapse text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 border-b">Student</th>
              <th className="text-left px-4 py-3 border-b">Admission Date</th>
              <th className="text-left px-4 py-3 border-b">Joining Date</th>
              <th className="text-left px-4 py-3 border-b">Payment Type</th>
              <th className="text-left px-4 py-3 border-b">Course</th>
              <th className="text-left px-4 py-3 border-b">Teacher</th>
              <th className="text-left px-4 py-3 border-b">WhatsApp</th>
              <th className="text-left px-4 py-3 border-b">Mode</th>
              <th className="text-right px-4 py-3 border-b">Collected (INR)</th>
              <th className="text-right px-4 py-3 border-b">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-gray-600">Loading…</td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-gray-600">No records found for this date range.</td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                const sid = typeof row.studentId === 'object' ? row.studentId?._id : row.studentId;
                const student = (typeof row.studentId === 'object' && row.studentId?.name)
                  ? row.studentId
                  : studentMap.get(String(sid));

                const studentName = getStudentName(student);
                const studentId = getStudentId(student);
                const admissionDate = getAdmissionDate(row);
                const joiningDate = getJoiningDate(student);
                const paymentType = getPaymentTypeLabel(row);
                const course = getCourseLabel(student);
                const teacher = getTeacherLabel(student);
                const whatsapp = getWhatsapp(student);
                const mode = getModeLabel(row);
                const amt = getCollectedAmount(row);

                return (
                  <tr key={row._id || `${studentId}-${toISODateOnly(admissionDate)}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 border-b">
                      <div className="font-medium">{studentName}</div>
                      <div className="text-gray-500">({studentId})</div>
                    </td>
                    <td className="px-4 py-3 border-b">{fmtDate(admissionDate)}</td>
                    <td className="px-4 py-3 border-b">{fmtDate(joiningDate)}</td>
                    <td className="px-4 py-3 border-b">{paymentType}</td>
                    <td className="px-4 py-3 border-b">{course}</td>
                    <td className="px-4 py-3 border-b">{teacher}</td>
                    <td className="px-4 py-3 border-b">{whatsapp}</td>
                    <td className="px-4 py-3 border-b">{mode}</td>
                    <td className="px-4 py-3 border-b text-right">INR {Number(amt).toLocaleString()}</td>
                    <td className="px-4 py-3 border-b text-right space-x-3">
                      {/* Keep these as placeholders unless you wire routes */}
                      <button className="text-blue-600 hover:underline" onClick={() => console.log('edit', row)}>
                        Edit
                      </button>
                      <button className="text-red-600 hover:underline" onClick={() => console.log('receipt', row)}>
                        Receipt
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Notes: This report uses <code>/api/payments</code> + <code>/api/students</code>. A “New Admission” is any payment containing an <code>admissionFee</code> fee line. Admission Date = payment <code>date</code> (unknown when missing). Joining Date = student <code>joinDate</code>.
      </div>
    </div>
  );
}